import React, { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { format } from "date-fns";
import {
  Webhook,
  Search,
  ArrowDownLeft,
  ArrowUpRight,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Clock,
  Eye,
  RefreshCw,
} from "lucide-react";
import { Input } from "@/shared/components/ui/input";
import { Badge } from "@/shared/components/ui/badge";
import { Button } from "@/shared/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/shared/components/ui/sheet";
import ResellerLayout from "@/shared/components/reseller/ResellerLayout";
import { useLanguage } from "@/shared/components/shared/LanguageContext";
import * as resellerService from "@/shared/api/services/resellerService";

const statusConfig = {
  received: { icon: Clock, color: "bg-blue-100 text-blue-700" },
  validated: { icon: CheckCircle2, color: "bg-emerald-100 text-emerald-700" },
  processed: { icon: CheckCircle2, color: "bg-emerald-100 text-emerald-700" },
  failed: { icon: XCircle, color: "bg-red-100 text-red-700" },
  invalid_signature: { icon: AlertTriangle, color: "bg-amber-100 text-amber-700" },
  duplicate: { icon: AlertTriangle, color: "bg-orange-100 text-orange-700" },
};

const translateStatus = (status, t) => {
  const statusMap = {
    received: "received",
    validated: "validated",
    processed: "processed",
    failed: "failed",
    invalid_signature: "invalidSignature",
    duplicate: "duplicate",
  };
  const translationKey = statusMap[status] || status;
  return t(translationKey) || status;
};

const formatJson = (value) => {
  if (!value) return "{}";
  try {
    const parsed = typeof value === "string" ? JSON.parse(value) : value;
    return JSON.stringify(parsed, null, 2);
  } catch {
    return typeof value === "string" ? value : JSON.stringify(value, null, 2);
  }
};

const deriveSource = (log) => {
  if (!log?.url) return "Unknown";
  try {
    const host = new URL(log.url).hostname.replace(/^www\./, "");
    if (host.includes("flexio")) return "Flexio";
    return host;
  } catch {
    return "Unknown";
  }
};

export default function ResellerWebhooks() {
  const { t } = useLanguage();
  const [search, setSearch] = useState("");
  const [directionFilter, setDirectionFilter] = useState("all");
  const [eventFilter, setEventFilter] = useState("all");
  const [selectedLog, setSelectedLog] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const {
    data: rawLogs = [],
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey: ["reseller-webhook-logs"],
    queryFn: () => resellerService.getWebhookLogs(1, 500),
    refetchInterval: 10000,
  });

  const logs = useMemo(() => {
    return (rawLogs || []).map((log) => ({
      ...log,
      source: deriveSource(log),
    }));
  }, [rawLogs]);

  const filteredWebhooks = logs.filter((log) => {
    if (
      search
      && !log.id?.toLowerCase().includes(search.toLowerCase())
      && !log.paymentId?.toLowerCase().includes(search.toLowerCase())
      && !log.source?.toLowerCase().includes(search.toLowerCase())
    ) {
      return false;
    }
    if (directionFilter !== "all" && log.direction !== directionFilter) return false;
    if (eventFilter !== "all" && log.event !== eventFilter) return false;
    return true;
  });

  const logsErrorMessage = useMemo(() => {
    if (!error) return t("failedToLoadLogs") || "Failed to load logs.";
    const rawMessage = String(error.message || "").trim();
    const cleanMessage = rawMessage.replace(/^Network error:\s*/i, "");
    return cleanMessage || (t("failedToLoadLogs") || "Failed to load logs.");
  }, [error, t]);

  const eventOptions = useMemo(() => {
    const unique = Array.from(new Set(logs.map((log) => log.event).filter(Boolean)));
    return unique.sort();
  }, [logs]);

  return (
    <ResellerLayout currentPage="ResellerWebhooks" resellerName="Asia Gaming Network">
      <div className="space-y-6">
        <div className="flex items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">{t("webhookLogs")}</h1>
            <p className="text-slate-500">{logs.length} webhook events</p>
          </div>
          <Button
            variant="outline"
            className="gap-2"
            onClick={() => {
              setIsRefreshing(true);
              refetch().finally(() => setIsRefreshing(false));
            }}
          >
            <RefreshCw className={`h-4 w-4 ${isRefreshing ? "animate-spin" : ""}`} />
            Refresh
          </Button>
        </div>

        <div className="bg-white rounded-xl p-4 border border-slate-100 flex flex-wrap gap-3">
          <div className="relative flex-1 min-w-[280px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              placeholder="Search by webhook ID, transaction ID, source..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10"
            />
          </div>
          <Select value={directionFilter} onValueChange={setDirectionFilter}>
            <SelectTrigger className="w-[130px]">
              <SelectValue placeholder={t("direction")} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("allDirection")}</SelectItem>
              <SelectItem value="inbound">{t("inbound")}</SelectItem>
              <SelectItem value="outbound">{t("outbound")}</SelectItem>
            </SelectContent>
          </Select>
          <Select value={eventFilter} onValueChange={setEventFilter}>
            <SelectTrigger className="w-[170px]">
              <SelectValue placeholder={t("event")} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("all")}</SelectItem>
              {eventOptions.map((event) => (
                <SelectItem key={event} value={event}>{event}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="bg-white rounded-xl border border-slate-100 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-100">
                <tr>
                  <th className="text-left text-xs font-medium text-slate-500 px-4 py-3">Webhook ID</th>
                  <th className="text-left text-xs font-medium text-slate-500 px-4 py-3">Direction</th>
                  <th className="text-left text-xs font-medium text-slate-500 px-4 py-3">Source</th>
                  <th className="text-left text-xs font-medium text-slate-500 px-4 py-3">Event</th>
                  <th className="text-left text-xs font-medium text-slate-500 px-4 py-3">Response</th>
                  <th className="text-left text-xs font-medium text-slate-500 px-4 py-3">Date</th>
                  <th className="text-left text-xs font-medium text-slate-500 px-4 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  Array.from({ length: 8 }).map((_, i) => (
                    <tr key={i} className="border-b border-slate-50">
                      <td colSpan={7} className="px-4 py-4">
                        <div className="h-4 bg-slate-100 rounded animate-pulse" />
                      </td>
                    </tr>
                  ))
                ) : isError ? (
                  <tr>
                    <td colSpan={7} className="text-center py-12 text-red-600 text-sm font-medium">
                      {logsErrorMessage}
                    </td>
                  </tr>
                ) : filteredWebhooks.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="text-center py-12 text-slate-500">No logs</td>
                  </tr>
                ) : (
                  filteredWebhooks.map((log, index) => (
                    <motion.tr
                      key={log.id}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: index * 0.01 }}
                      className="border-b border-slate-50 hover:bg-slate-50"
                    >
                      <td className="px-4 py-3">
                        <p className="font-mono text-sm text-slate-900">{log.id}</p>
                      </td>
                      <td className="px-4 py-3">
                        <Badge
                          variant="outline"
                          className={`gap-1 ${log.direction === "inbound" ? "border-blue-200 text-blue-700" : "border-emerald-200 text-emerald-700"}`}
                        >
                          {log.direction === "inbound" ? (
                            <ArrowDownLeft className="h-3 w-3" />
                          ) : (
                            <ArrowUpRight className="h-3 w-3" />
                          )}
                          {t(log.direction)}
                        </Badge>
                      </td>
                      <td className="px-4 py-3 text-sm font-medium text-slate-700">{log.source}</td>
                      <td className="px-4 py-3">
                        <Badge variant="outline" className="text-xs">{log.event}</Badge>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`text-sm font-medium ${log.httpStatus >= 200 && log.httpStatus < 300 ? "text-emerald-600" : "text-red-600"}`}>
                          {log.httpStatus || "-"}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-500">
                        {log.createdAt ? format(new Date(log.createdAt), "MMM d, yyyy HH:mm:ss") : "-"}
                      </td>
                      <td className="px-4 py-3">
                        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setSelectedLog(log)}>
                          <Eye className="h-4 w-4" />
                        </Button>
                      </td>
                    </motion.tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <Sheet open={!!selectedLog} onOpenChange={() => setSelectedLog(null)}>
        <SheetContent className="w-full sm:max-w-xl overflow-y-auto">
          <SheetHeader>
            <SheetTitle>{t("webhookDetails")}</SheetTitle>
          </SheetHeader>

          {selectedLog && (
            <div className="mt-6 space-y-6">
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-slate-500">{t("webhookId")}</p>
                    <p className="font-mono text-sm break-all">{selectedLog.id}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">{t("transactionId")}</p>
                    <p className="font-mono text-sm break-all">{selectedLog.paymentId || t("nA")}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">{t("direction")}</p>
                    <Badge variant="outline">{t(selectedLog.direction)}</Badge>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">{t("status")}</p>
                    <Badge className={statusConfig[selectedLog.status]?.color || "bg-slate-100"}>
                      {translateStatus(selectedLog.status, t)}
                    </Badge>
                  </div>
                </div>

                <div>
                  <p className="text-sm text-slate-500 mb-1">{t("url")}</p>
                  <p className="font-mono text-sm bg-slate-50 p-2 rounded break-all">{selectedLog.url || t("nA")}</p>
                </div>

                <div>
                  <p className="text-sm text-slate-500 mb-1">{t("requestHeaders")}</p>
                  <pre className="text-xs bg-slate-900 text-slate-100 p-4 rounded-lg overflow-x-auto">{"{}"}</pre>
                </div>

                <div>
                  <p className="text-sm text-slate-500 mb-1">{t("requestPayload")}</p>
                  <pre className="text-xs bg-slate-900 text-slate-100 p-4 rounded-lg overflow-x-auto max-h-[220px]">
                    {formatJson(selectedLog.requestPayload)}
                  </pre>
                </div>

                <div>
                  <p className="text-sm text-slate-500 mb-1">{t("responseStatus")}</p>
                  <div className="flex items-center gap-2 mb-2">
                    <Badge variant="outline">{t("status")}: {selectedLog.httpStatus || t("nA")}</Badge>
                  </div>
                  <pre className="text-xs bg-slate-900 text-slate-100 p-4 rounded-lg overflow-x-auto max-h-[220px]">
                    {selectedLog.responseBody || t("noResponseBody")}
                  </pre>
                </div>

                {selectedLog.errorMessage && (
                  <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                    <p className="text-sm font-medium text-red-800">{t("error")}</p>
                    <p className="text-sm text-red-700">{selectedLog.errorMessage}</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>
    </ResellerLayout>
  );
}
